/** Normaliza texto para comparação (trim + colapsar espaços). */
export function normalizeCompareText(text) {
    if (text == null) return "";
    return String(text).trim().replace(/\s+/g, " ");
}

/** Mediana de números finitos; null se vazio. */
export function medianOf(values) {
    const nums = values.filter((v) => Number.isFinite(v));
    if (nums.length === 0) return null;
    const sorted = [...nums].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    if (sorted.length % 2 === 0) {
        return (sorted[mid - 1] + sorted[mid]) / 2;
    }
    return sorted[mid];
}

function shortModelLabel(id) {
    if (!id) return "?";
    const s = String(id).replace(/^openrouter:/, "");
    const parts = s.split("/");
    return parts[parts.length - 1] || s;
}

function slotLabel(slot, models = []) {
    const meta = models.find((m) => m.slot === slot);
    if (meta?.label) return meta.label;
    if (meta?.id) return shortModelLabel(meta.id);
    return `slot ${slot}`;
}

function clipText(text, max = 60) {
    const s = normalizeCompareText(text);
    if (!s) return "—";
    return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

function summaryForSlots(rows, models, formatValue) {
    return rows.map((r) => `${slotLabel(r.slot, models)}=${formatValue(r)}`).join(" · ");
}

function groupResultsByTurn(results) {
    const byTurn = new Map();
    for (const row of results || []) {
        if (!row?.turnId) continue;
        if (!byTurn.has(row.turnId)) byTurn.set(row.turnId, []);
        byTurn.get(row.turnId).push(row);
    }
    return byTurn;
}

/**
 * Calcula discrepâncias entre slots para cada turnId com ≥2 resultados.
 * Regras: pass, intent/handoff, texto, latência (≥2× mediana), custo (≥2× mediana).
 * Summaries com valores lado a lado.
 */
export function computeDiscrepancies(results = [], models = []) {
    const discrepancies = [];
    const byTurn = groupResultsByTurn(results);

    for (const [turnId, rows] of byTurn.entries()) {
        if (rows.length < 2) continue;

        const passes = rows.map((r) => r.pass);
        const uniquePass = new Set(
            passes.map((p) => (p === true ? "pass" : p === false ? "fail" : "unknown"))
        );
        if (uniquePass.size > 1) {
            discrepancies.push({
                turnId,
                type: "pass",
                summary: `pass: ${summaryForSlots(rows, models, (r) => (r.pass ? "✓" : "✗"))}`,
                slots: rows.map((r) => r.slot),
            });
        }

        const intents = rows.map((r) => String(r.intent ?? ""));
        const handoffs = rows.map((r) => (r.handoff_now == null ? "null" : String(r.handoff_now)));
        const intentSet = new Set(intents);
        const handoffSet = new Set(handoffs);
        if (intentSet.size > 1 || handoffSet.size > 1) {
            discrepancies.push({
                turnId,
                type: "intent",
                summary: `intent/handoff: ${summaryForSlots(
                    rows,
                    models,
                    (r) => `${r.intent || "—"}${r.handoff_now ? "/handoff" : ""}`
                )}`,
                slots: rows.map((r) => r.slot),
            });
        }

        const outputs = rows.map((r) => normalizeCompareText(r.output));
        const outputSet = new Set(outputs);
        if (outputSet.size > 1) {
            discrepancies.push({
                turnId,
                type: "texto",
                summary: `texto: ${summaryForSlots(rows, models, (r) => `"${clipText(r.output)}"`)}`,
                slots: rows.map((r) => r.slot),
            });
        }

        const latencyRows = rows.filter((r) => Number.isFinite(Number(r.latencyMs)));
        if (latencyRows.length >= 2) {
            const slow = latencyRows.filter((r) => {
                const others = latencyRows
                    .filter((x) => x.slot !== r.slot)
                    .map((x) => Number(x.latencyMs));
                const med = medianOf(others);
                return med != null && med > 0 && Number(r.latencyMs) >= 2 * med;
            });
            if (slow.length > 0) {
                discrepancies.push({
                    turnId,
                    type: "latency",
                    summary: `latência (≥2× mediana): ${summaryForSlots(
                        latencyRows,
                        models,
                        (r) => `${(Number(r.latencyMs) / 1000).toFixed(1)}s`
                    )}`,
                    slots: slow.map((r) => r.slot),
                });
            }
        }

        const costRows = rows.filter((r) => {
            const c = r.usage?.cost_usd ?? r.cost_usd;
            return Number.isFinite(Number(c));
        });
        if (costRows.length >= 2) {
            const expensive = costRows.filter((r) => {
                const cost = Number(r.usage?.cost_usd ?? r.cost_usd);
                const others = costRows
                    .filter((x) => x.slot !== r.slot)
                    .map((x) => Number(x.usage?.cost_usd ?? x.cost_usd));
                const med = medianOf(others);
                return med != null && med > 0 && cost >= 2 * med;
            });
            if (expensive.length > 0) {
                discrepancies.push({
                    turnId,
                    type: "cost",
                    summary: `custo (≥2× mediana): ${summaryForSlots(
                        costRows,
                        models,
                        (r) => {
                            const c = Number(r.usage?.cost_usd ?? r.cost_usd);
                            return `$${c.toFixed(4)}`;
                        }
                    )}`,
                    slots: expensive.map((r) => r.slot),
                });
            }
        }
    }

    return discrepancies;
}

/** Slots com discrepância em qualquer turno. */
export function slotsWithDiscrepancies(discrepancies = []) {
    const set = new Set();
    for (const d of discrepancies) {
        for (const s of d.slots || []) set.add(s);
    }
    return set;
}
